export enum Genero {
  MASCULINO = 'masculino',
  FEMENINO = 'femenino',
  OTRO = 'otro',
}

export enum EstadoCivil {
  SOLTERO = 'soltero',
  CASADO = 'casado',
  DIVORCIADO = 'divorciado',
  VIUDO = 'viudo',
  UNION_LIBRE = 'union_libre',
}

export interface Patient {
  id: string;
  userId: string | null;
  cedula: string;
  nombres: string;
  apellidos: string;
  email: string | null;
  fechaNacimiento: string;
  genero: Genero;
  telefono: string | null;
  telefonoEmergencia: string | null;
  direccion: string | null;
  ciudad: string | null;
  provincia: string | null;
  codigoPostal: string | null;
  ocupacion: string | null;
  estadoCivil: EstadoCivil | null;
  createdAt: string;
  updatedAt: string;
}

export type CreatePatientData = {
  cedula: string;
  nombres: string;
  apellidos: string;
  fechaNacimiento: string;
  genero: Genero;
  email?: string;
  telefono?: string;
  telefonoEmergencia?: string;
  direccion?: string;
  ciudad?: string;
  provincia?: string;
  codigoPostal?: string;
  ocupacion?: string;
  estadoCivil?: EstadoCivil;
};

export type UpdatePatientData = Partial<CreatePatientData>;

// ─── Antecedentes ─────────────────────────────────────────────────────────────

export interface AntecedentesHeredofamiliar {
  id: string;
  patientId: string;
  diabetes: boolean;
  diabetesFamiliar: string | null;
  diabetesNotas: string | null;
  hipertension: boolean;
  hipertensionFamiliar: string | null;
  hipertensionNotas: string | null;
  cardiopatias: boolean;
  cardiopatiasFamiliar: string | null;
  cardiopatiasNotas: string | null;
  cancer: boolean;
  cancerTipo: string | null;
  cancerFamiliar: string | null;
  cancerNotas: string | null;
  enfermedadesRespiratorias: boolean;
  enfermedadesRespiratoriasTipo: string | null;
  enfermedadesRespiratoriasFamiliar: string | null;
  enfermedadesRenales: boolean;
  enfermedadesRenalesFamiliar: string | null;
  enfermedadesRenalesNotas: string | null;
  enfermedadesNeurologicas: boolean;
  enfermedadesNeurologicasTipo: string | null;
  enfermedadesNeurologicasFamiliar: string | null;
  enfermedadesMentales: boolean;
  enfermedadesMentalesTipo: string | null;
  enfermedadesMentalesFamiliar: string | null;
  otros: Array<{ enfermedad: string; familiar?: string; notas?: string }> | null;
  registradoPorId: string | null;
  fechaRegistro: string;
  ultimaActualizacion: string;
}

export interface AntecedentesPatologico {
  id: string;
  patientId: string;
  diabetesMellitus: boolean;
  diabetesTipo: string | null;
  diabetesAnioDiagnostico: number | null;
  diabetesTratamiento: string | null;
  hipertensionArterial: boolean;
  hipertensionAnioDiagnostico: number | null;
  hipertensionTratamiento: string | null;
  cardiopatias: boolean;
  cardiopatiasTipo: string | null;
  enfermedadesRespiratorias: boolean;
  enfermedadesRespiratoriasTipo: string | null;
  enfermedadesRenales: boolean;
  enfermedadesRenalesTipo: string | null;
  cancer: boolean;
  cancerTipo: string | null;
  cancerRemision: boolean | null;
  tuberculosis: boolean;
  hepatitis: boolean;
  hepatitisTipo: string | null;
  vihSida: boolean;
  covid19: boolean;
  covid19Severidad: string | null;
  covid19Secuelas: string | null;
  epilepsia: boolean;
  epilepsiaControlada: boolean | null;
  accidenteCerebrovascular: boolean;
  depresion: boolean;
  ansiedad: boolean;
  cirugias: object[] | null;
  hospitalizaciones: object[] | null;
  traumatismos: object[] | null;
  alergiasMedicamentos: object[] | null;
  transfusiones: boolean;
  otros: object[] | null;
  registradoPorId: string | null;
  fechaRegistro: string;
  ultimaActualizacion: string;
}

export interface AntecedentesNoPatologico {
  id: string;
  patientId: string;
  tabaquismo: boolean;
  tabaquismoTipo: string | null;
  tabaquismoCigarrillosDia: number | null;
  alcoholismo: boolean;
  alcoholismoFrecuencia: string | null;
  alcoholismoCantidad: string | null;
  drogas: boolean;
  drogasTipo: string | null;
  cafe: boolean;
  cafeTazasDia: number | null;
  actividadFisica: boolean;
  actividadFisicaTipo: string | null;
  actividadFisicaFrecuencia: string | null;
  actividadFisicaDuracionMinutos: number | null;
  alimentacionTipo: string | null;
  alimentacionComidasDia: number;
  horasSuenoPromedio: number | null;
  calidadSueno: string | null;
  esquemaVacunacionCompleto: boolean | null;
  otrosHabitos: string | null;
  registradoPorId: string | null;
  fechaRegistro: string;
  ultimaActualizacion: string;
}

export interface AntecedentesGineco {
  id: string;
  patientId: string;
  menarcaEdad: number | null;
  fechaUltimaMenstruacion: string | null;
  cicloMenstrualRegular: boolean | null;
  cicloMenstrualDuracionDias: number;
  dismenorrea: boolean;
  dismenorreaIntensidad: string | null;
  menopausia: boolean;
  menopausiaEdad: number | null;
  metodoAnticonceptivoActual: string | null;
  gestas: number;
  partos: number;
  cesareas: number;
  abortos: number;
  hijosVivos: number;
  embarazoActual: boolean;
  embarazoActualSemanas: number | null;
  citologiaUltimaFecha: string | null;
  citologiaResultado: string | null;
  mamografiaUltimaFecha: string | null;
  otros: string | null;
  registradoPorId: string | null;
  fechaRegistro: string;
  ultimaActualizacion: string;
}

export interface AntecedentesCompletos {
  heredofamiliares: AntecedentesHeredofamiliar | null;
  patologicos: AntecedentesPatologico | null;
  noPatologicos: AntecedentesNoPatologico | null;
  ginecoObstetricos: AntecedentesGineco | null;
}

// ─── Tarjetero Índice ──────────────────────────────────────────────────────────

export enum EstadoTarjetero {
  ACTIVO = 'activo',
  INACTIVO = 'inactivo',
  ARCHIVADO = 'archivado',
}

export interface PatientResumen {
  id: string;
  cedula: string;
  nombres: string;
  apellidos: string;
  genero: Genero;
  email?: string | null;
  telefono?: string | null;
}

export interface TarjeteroIndice {
  id: string;
  codigoHc: string;
  pacienteId: string;
  paciente: PatientResumen;
  medicoResponsableId: string | null;
  estado: EstadoTarjetero;
  observaciones: string | null;
  fechaApertura: string;
  createdAt: string;
  updatedAt: string;
}

export type CreateTarjeteroDto = {
  medicoResponsableId?: string;
  observaciones?: string;
};

export type UpdateTarjeteroDto = {
  medicoResponsableId?: string;
  estado?: EstadoTarjetero;
  observaciones?: string;
};

// ─── Current DB User ──────────────────────────────────────────────────────────

export interface CurrentDbUser {
  id: string;
  email: string;
  role: string;
  cedula: string | null;
  nombres: string | null;
  apellidos: string | null;
  externalAuthId: string | null;
  hasAvailability: boolean;
  capacidadAtencionParalela: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Clinical Episodes ────────────────────────────────────────────────────────

export enum EstadoEpisodio {
  ABIERTO = 'abierto',
  EN_TRATAMIENTO = 'en_tratamiento',
  CERRADO = 'cerrado',
  ARCHIVADO = 'archivado',
}

export type TipoPrueba = 'laboratorio' | 'imagen' | 'electrocardiograma' | 'biopsia' | 'otra';

export interface Anamnesis {
  motivo:    string | null;
  evolucion: string | null;
  sintomas:  string | null;
}

export interface EpisodeSignosVitales {
  peso:                  number | null;
  talla:                 number | null;
  temperatura:           number | null;
  presionArterial:       string | null;
  frecuenciaCardiaca:    number | null;
  frecuenciaRespiratoria: number | null;
}

export interface PruebasSolicitada {
  tipo:        TipoPrueba;
  descripcion: string;
}

export interface ClinicalEpisode {
  id: string;
  tarjeteroId: string;
  codigoHc: string;
  pacienteId: string;
  paciente?: PatientResumen;
  profesionalId: string;
  estado: EstadoEpisodio;
  motivoConsulta: string;
  diagnosticoPrincipal: string | null;
  codigoCie10: string | null;
  diagnosticoSecundario: string | null;
  diagnosticoDiferencial: string | null;
  notaApertura: string | null;
  notaCierre: string | null;
  fechaApertura: string;
  fechaCierre: string | null;
  appointmentId: string | null;
  anamnesis: Anamnesis | null;
  signosVitales: EpisodeSignosVitales | null;
  pruebasSolicitadas: PruebasSolicitada[] | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateEpisodioDto = {
  anamnesis: {
    motivo: string;
    evolucion?: string;
    sintomas?: string;
  };
  signosVitales?: {
    peso: number;
    talla: number;
    temperatura: number;
    presionArterial: string;
    frecuenciaCardiaca: number;
    frecuenciaRespiratoria: number;
  };
  impressionDiagnostica?: string;
};

export type UpdateEpisodioDto = {
  motivoConsulta?: string;
  diagnosticoPrincipal?: string;
  codigoCie10?: string;
  diagnosticoSecundario?: string;
  diagnosticoDiferencial?: string;
  notaApertura?: string;
  estado?: EstadoEpisodio;
  anamnesis?: {
    motivo: string;
    evolucion: string;
    sintomas: string;
  };
  pruebasSolicitadas?: PruebasSolicitada[] | null;
};

export type CloseEpisodioDto = {
  notaCierre: string;
  diagnosticoPrincipal?: string;
  codigoCie10?: string;
};

// ─── SOAP Notes ───────────────────────────────────────────────────────────────

export interface SignosVitales {
  ta: string | null;
  fc: number | null;
  fr: number | null;
  temperatura: number | null;
  spo2: number | null;
  peso: number | null;
  talla: number | null;
}

export type SoapSubjetivo = Record<string, unknown>;
export type SoapObjetivo  = Record<string, unknown>;
export type SoapAnalisis  = Record<string, unknown>;
export type SoapPlan      = Record<string, unknown>;

export interface SoapNote {
  id: string;
  episodeId: string;
  codigoHc: string;
  pacienteId: string;
  profesionalId: string;
  numeroSesion: number;
  fechaSesion: string;
  subjetivo: SoapSubjetivo;
  objetivo: SoapObjetivo;
  analisis: SoapAnalisis;
  plan: SoapPlan;
  observaciones: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateSoapNoteDto = {
  subjective: Record<string, unknown>;
  objective: Record<string, unknown>;
  assessment: Record<string, unknown>;
  plan: Record<string, unknown>;
};

export type UpdateSoapNoteDto = Partial<CreateSoapNoteDto>;

// ─── Physical Evaluations ─────────────────────────────────────────────────────

export enum TipoEvaluacion {
  GERIATRICA     = 'GERIATRICA',
  TRAUMATOLOGICA = 'TRAUMATOLOGICA',
  DEPORTIVA      = 'DEPORTIVA',
  PEDIATRICA     = 'PEDIATRICA',
}

export interface PruebaEspecifica {
  resultado: 'positivo' | 'negativo' | 'dudoso';
  notas?: string;
}

export interface PhysicalEvaluation {
  id: string;
  episodeId: string;
  codigoHc: string;
  pacienteId: string;
  profesionalId: string;
  numeroEvaluacion: number;
  fechaEvaluacion: string;
  tipoEvaluacion: TipoEvaluacion | null;
  evaluationData: Record<string, unknown> | null;
  rangoMovimiento: Record<string, number> | null;
  fuerzaMuscular: Record<string, number> | null;
  escalaDolor: number | null;
  pruebasEspecificas: Record<string, PruebaEspecifica> | null;
  inspeccion: string | null;
  palpacion: string | null;
  diagnostico: string | null;
  observaciones: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateEvaluacionDto = {
  fechaEvaluacion: string;
  profesionalId: string;
  tipoEvaluacion?: TipoEvaluacion;
  evaluationData?: Record<string, unknown>;
  escalaDolor?: number;
  rangoMovimiento?: Record<string, number>;
  fuerzaMuscular?: Record<string, number>;
  pruebasEspecificas?: Record<string, PruebaEspecifica>;
  inspeccion?: string;
  palpacion?: string;
  diagnostico?: string;
  observaciones?: string;
};

export type UpdateEvaluacionDto = Partial<Omit<CreateEvaluacionDto, 'profesionalId'>>;

// ─── Treatment Plans ──────────────────────────────────────────────────────────

export interface CreateTreatmentPlanDto {
  clinicalEpisodeId: string;
  totalSessions: number;
  frequencyDescription: string;
  objectives: {
    shortTerm: string;
    longTerm: string;
  };
}

export interface TreatmentPlanCreatedResponse {
  id: string;
  clinicalEpisodeId: string;
  status: string;
  totalSessions: number;
  currentSessionIndex: number;
  version: number;
  createdAt: string;
}

export enum EstadoPlan {
  BORRADOR   = 'borrador',
  ACTIVO     = 'activo',
  COMPLETADO = 'completado',
  CANCELADO  = 'cancelado',
}

export interface Exercise {
  id: string;
  planId: string;
  nombre: string;
  descripcion: string | null;
  series: number | null;
  repeticiones: number | null;
  duracionSegundos: number | null;
  orden: number;
  observaciones: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExerciseDosage {
  series: number;
  repetitions?: number;
  executionTime?: number;
  load: string;
  restTime?: string;
  notes: string;
}

export interface TreatmentExercise {
  id: string;
  planId: string;
  baseExerciseId: string | null;
  name: string;
  sortOrder: number;
  dosage: ExerciseDosage;
}

export interface PrescribeExerciseDto {
  baseExerciseId?: string;
  name: string;
  dosage: ExerciseDosage;
}

export interface UpdateExerciseDosageDto {
  name?: string;
  dosage?: Partial<ExerciseDosage>;
}

export interface TreatmentPlan {
  id: string;
  episodeId: string;
  codigoHc: string;
  pacienteId: string;
  paciente?: PatientResumen;
  profesionalId: string;
  numeroPlan: number;
  /** Campo del API nuevo (TypeORM property: status → DB column: estado) */
  status?: EstadoPlan;
  /** Campo del API viejo (keep for backward compat) */
  estado: EstadoPlan;
  objetivoTerapeutico: string;
  therapeuticObjectives?: { shortTerm: string; longTerm: string } | null;
  totalSessions?: number;
  currentSessionIndex?: number;
  duracionEstimadaSemanas: number | null;
  frecuenciaSemanal: number | null;
  fechaInicio: string | null;
  fechaFin: string | null;
  progresoPorcentaje: number;
  appointmentId: string | null;
  observaciones: string | null;
  exercises: Exercise[];
  createdAt: string;
  updatedAt: string;
}

export type CreatePlanDto = {
  profesionalId: string;
  objetivoTerapeutico: string;
  duracionEstimadaSemanas?: number;
  frecuenciaSemanal?: number;
  fechaInicio?: string;
  fechaFin?: string;
  appointmentId?: string;
  observaciones?: string;
};

export type UpdatePlanDto = {
  objetivoTerapeutico?: string;
  duracionEstimadaSemanas?: number;
  frecuenciaSemanal?: number;
  fechaInicio?: string;
  fechaFin?: string;
  appointmentId?: string;
  progresoPorcentaje?: number;
  estado?: EstadoPlan.COMPLETADO | EstadoPlan.CANCELADO;
  observaciones?: string;
};

// ─── HU03 — Plan Activation ──────────────────────────────────────────────────

export type DiasSemana =
  | 'LUNES' | 'MARTES' | 'MIERCOLES' | 'JUEVES'
  | 'VIERNES' | 'SABADO' | 'DOMINGO';

export interface SchedulePreferenceInput {
  dayOfWeek: DiasSemana;
  startTime: string; // "HH:mm"
}

export interface ActivationOverrideInput {
  sessionIndex: number;
  fecha: string; // "YYYY-MM-DD"
  hora: string;  // "HH:mm"
}

export interface ActivatePlanInput {
  startDate: string; // "YYYY-MM-DD"
  duracionMinutos?: number;
  schedulePreferences: SchedulePreferenceInput[];
  overrides?: ActivationOverrideInput[];
}

export interface SuggestedSlotAlternative {
  inicio: string;
  fin: string;
  ocupados: number;
  capacidad: number;
}

export interface ProposedSlot {
  sessionIndex: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  hasConflict: boolean;
  suggestedAlternatives?: SuggestedSlotAlternative[];
}

export interface PreviewScheduleResponse {
  proposedSlots: ProposedSlot[];
  totalSessions: number;
  startDate: string;
  endDate: string;
  conflictCount: number;
  canActivate: boolean;
}

export interface ActivatePlanResponse {
  message: string;
  planId: string;
  status: string;
}

// ─────────────────────────────────────────────────────────────────────────────

export type CreateExerciseDto = {
  nombre: string;
  descripcion?: string;
  series?: number;
  repeticiones?: number;
  duracionSegundos?: number;
  observaciones?: string;
};

export type UpdateExerciseDto = Partial<CreateExerciseDto>;

export type ReorderExercisesDto = {
  orden: Array<{ id: string; orden: number }>;
};

// ─── Appointments ─────────────────────────────────────────────────────────────

export enum TipoCita {
  PRIMERA_VEZ = 'PRIMERA_VEZ',
  SEGUIMIENTO = 'SEGUIMIENTO',
  INTERCONSULTA = 'INTERCONSULTA',
}

export enum EstadoCita {
  CONFIRMADA = 'CONFIRMADA',
  CANCELADA = 'CANCELADA',
  COMPLETADA = 'COMPLETADA',
  REPROGRAMADA = 'REPROGRAMADA',
  NO_ASISTIO = 'NO_ASISTIO',
}

export interface Appointment {
  id: string;
  patientId: string;
  patient: PatientResumen;
  professionalId: string;
  scheduledAt: string;
  durationMinutes: number;
  tipoCita: TipoCita;
  estado: EstadoCita;
  motivo: string | null;
  notas: string | null;
  motivoCancelacion: string | null;
  episodeId: string | null;
  sessionPaymentId: string | null;
  sessionId: string | null;
  treatmentPlanId: string | null;
  sessionInfo: { numeroSesion: number; totalSessions: number } | null;
  reprogramadaDeId: string | null;
  nuevaCitaId: string | null;
  motivoReprogramacion: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EnrichedAppointment extends Appointment {
  episode: { codigoHc: string; motivoConsulta: string; estado: string } | null;
  session: { id: string; numeroSesion: number; estado: string; plan: { nombre: string } | null } | null;
  payment: { monto: number; estadoPago: string } | null;
  reprogramadaDe: { id: string; scheduledAt: string } | null;
  nuevaCita: { id: string; scheduledAt: string; estado: EstadoCita } | null;
}

export type BookingType = 'SDA' | 'PRE_BOOK' | 'EMERGENCIA';

export type CreateAppointmentDto = {
  patientId: string;
  professionalId: string;
  scheduledAt: string;
  tipoCita: TipoCita;
  motivo?: string;
  notas?: string;
  bookingType?: BookingType;
  episodeId?: string;
  planId?: string;
  sessionId?: string;
};

export type UpdateAppointmentDto = {
  scheduledAt?: string;
  durationMinutes?: number;
  professionalId?: string;
  motivo?: string;
  notas?: string;
};

export type CancelAppointmentDto = {
  motivoCancelacion: string;
};

export type CompleteAppointmentDto = {
  monto: number;
  episodeId?: string;
  planId?: string;
};

export type CompleteAppointmentResponse = {
  appointment: Appointment;
  sessionId: string | null;
};

export type RescheduleAppointmentDto = {
  scheduledAt: string;
  motivo?: string;
};

export type RescheduleAppointmentResponse = {
  original: Appointment;
  nueva: Appointment;
};

// ─── Sessions ─────────────────────────────────────────────────────────────────

export enum TipoSesion {
  FISIOTERAPIA = 'FISIOTERAPIA',
  EVALUACION_FISICA = 'EVALUACION_FISICA',
  INTERCONSULTA = 'INTERCONSULTA',
  CONSULTA_MEDICA = 'CONSULTA_MEDICA',
}

export enum EstadoSesion {
  PROGRAMADA = 'PROGRAMADA',
  EN_CURSO = 'EN_CURSO',
  ATENDIDA = 'ATENDIDA',
  COMPLETADA = 'COMPLETADA',
  CANCELADA = 'CANCELADA',
  NO_SHOW = 'NO_SHOW',
}

export interface Session {
  id: string;
  planId: string | null;
  episodeId: string;
  codigoHc: string;
  pacienteId: string;
  profesionalId: string;
  tipo: TipoSesion;
  estado: EstadoSesion;
  numeroSesion: number;
  fechaSesion: string;
  appointmentId: string | null;
  soapNote: SoapNote | null;
  physicalEval: PhysicalEvaluation | null;
  interconsult: any | null;
  observaciones: string | null;
  motivo: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ReasonSessionDto = {
  motivo?: string;
};

export type CreateSessionDto = {
  tipo: TipoSesion;
  fechaSesion: string;
  profesionalId: string;
  appointmentId?: string;
  observaciones?: string;
};

export type UpdateSessionDto = {
  fechaSesion?: string;
  profesionalId?: string;
  estado?: EstadoSesion;
  observaciones?: string;
};

export interface SessionListResponse {
  data: Session[];
  meta: { total: number; page: number; limit: number; pages: number };
}

export type SoapSubjetivoDto = {
  motivoSesion: string;
  evaDolor?: number;
  sintomasReferidos?: string;
};

export type SoapObjetivoDto = {
  hallazgosExamenFisico?: string;
  rangoMovimiento?: string;
  fuerzaMuscular?: string;
  otrosHallazgos?: string;
};

export type SoapAnalisisDto = {
  diagnosticoFisioterapeutico?: string;
  progresoVsAnterior?: string;
  respuestaTratamiento?: string;
};

export type SoapPlanDto = {
  tecnicasAplicadas?: string;
  ejerciciosIndicados?: string;
  objetivosProximaSesion?: string;
};

export type CompleteSessionDto = {
  subjetivo: SoapSubjetivoDto;
  objetivo: SoapObjetivoDto;
  analisis?: SoapAnalisisDto;
  plan?: SoapPlanDto;
  observaciones?: string;
};

export type SoapOption = 'COMPLETE_NOW' | 'DEFER_SOAP';

export type RegisterSessionChargeDto = {
  serviceItemId: string;
  soapOption: SoapOption;
  discountAmount?: number;
  discountPercent?: number;
  discountReason?: string;
};

export interface RegisterSessionChargeBilling {
  accountId: string;
  serviceItemId: string;
  serviceItemName: string;
  basePrice: number;
  discountAmount: number;
  totalAmount: number;
  discountReason: string | null;
  updatedBalance: BillingAccountBalance;
}

export interface RegisterSessionChargeResponse {
  session: Session;
  billing: RegisterSessionChargeBilling;
}

// ─── Payments ─────────────────────────────────────────────────────────────────

export enum EstadoPago {
  PENDIENTE = 'PENDIENTE',
  PAGADO    = 'PAGADO',
  PARCIAL   = 'PARCIAL',
}

export enum MetodoPago {
  EFECTIVO      = 'EFECTIVO',
  TRANSFERENCIA = 'TRANSFERENCIA',
  TARJETA       = 'TARJETA',
  SEGURO        = 'SEGURO',
}

export enum ChargeSourceType {
  PLAN_ACTIVATION = 'PLAN_ACTIVATION',
  SESSION         = 'SESSION',
  EVALUATION      = 'EVALUATION',
  MANUAL          = 'MANUAL',
}

export interface BillingCharge {
  id: string;
  billingAccountId: string;
  amount: number;
  description: string;
  sourceType: ChargeSourceType;
  sourceId: string | null;
  basePrice: number | null;
  discountAmount: number | null;
  discountReason: string | null;
  createdAt: string;
}

export interface BillingAccountBalance {
  totalCharged: number;
  totalPaid: number;
  currentDebt: number;
}

export interface BillingAccount {
  id?: string;
  patientId: string;
  status?: string;
  balance: BillingAccountBalance;
  charges: BillingCharge[];
  updatedAt?: string;
  createdAt?: string;
}

export interface SessionPayment {
  id: string;
  appointmentId: string | null;
  appointment?: Appointment;
  monto: number;
  estadoPago: EstadoPago;
  metodoPago: MetodoPago | null;
  fechaPago: string | null;
  createdAt: string;
}

export interface Invoice {
  id: string;
  paymentId: string | null;
  numeroFactura: string | null;
  rucEmisor: string | null;
  claveAcceso: string | null;
  autorizacionSri: string | null;
  xmlFactura: string | null;
  createdAt: string;
}

export type UpdateSessionPaymentDto = {
  estadoPago?: EstadoPago;
  metodoPago?: MetodoPago;
  monto?: number;
  fechaPago?: string;
};

export type CreateInvoiceDto = {
  numeroFactura?: string;
  rucEmisor?: string;
  claveAcceso?: string;
  autorizacionSri?: string;
  xmlFactura?: string;
};

export interface PaymentPaginatedResponse {
  data: SessionPayment[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export enum PaymentMethodType {
  EFECTIVO      = 'EFECTIVO',
  TARJETA       = 'TARJETA',
  TRANSFERENCIA = 'TRANSFERENCIA',
}

export interface PaymentReceiptMethod {
  type: PaymentMethodType;
  referenceNumber: string | null;
}

export enum ReceiptStatus {
  PENDIENTE  = 'PENDIENTE',
  FACTURADO  = 'FACTURADO',
}

export interface PaymentReceipt {
  id: string;
  billingAccountId: string;
  receiptNumber: string;
  amount: number;
  paidAt: string;
  paymentMethod: PaymentReceiptMethod;
  receiptStatus: ReceiptStatus;
  createdAt: string;
}

export type RecordPaymentDto = {
  amount: number;
  paymentMethod: PaymentMethodType;
  referenceNumber?: string;
};

export interface RecordPaymentResponse {
  account: BillingAccount;
  receipt: PaymentReceipt;
}

// ─── Cuentas por Cobrar (AR) ─────────────────────────────────────────────────

export interface DebtorPatient {
  nombres: string;
  apellidos: string;
  cedula: string | null;
}

export interface DebtorProfessional {
  id: string;
  nombres: string;
  apellidos: string;
}

export interface DebtorBalance {
  totalCharged: number;
  totalPaid: number;
  currentDebt: number;
}

export interface DebtorAccount {
  billingAccountId: string;
  patientId: string;
  patient: DebtorPatient;
  lastProfessional: DebtorProfessional | null;
  balance: DebtorBalance;
}

// ─── Mi Caja Diaria ───────────────────────────────────────────────────────────

export interface DailyBoxTotals {
  efectivo: number;
  tarjeta: number;
  transferencia: number;
  total: number;
}

export interface DailyBoxReceiptRow {
  receiptNumber: string;
  paidAt: string;
  amount: number;
  methodType: string;
  referenceNumber: string | null;
  patientNombres: string;
  patientApellidos: string;
  patientCedula: string;
  serviceDescription: string | null;
}

export interface DailyBox {
  totals: DailyBoxTotals;
  receipts: DailyBoxReceiptRow[];
}

// ─── Catálogo de Servicios (Tarifario) ────────────────────────────────────────

export enum EstadoServicio {
  ACTIVO   = 'activo',
  INACTIVO = 'inactivo',
}

export interface ServiceItem {
  id: string;
  name: string;
  code: string;
  basePrice: number;
  description: string | null;
  status: EstadoServicio;
  createdAt: string;
  updatedAt: string;
}

export type CreateServiceItemDto = {
  name: string;
  code: string;
  basePrice: number;
  description?: string;
};

export type UpdateServiceItemDto = Partial<CreateServiceItemDto> & {
  status?: EstadoServicio;
};

export interface ServiceItemPaginatedResponse {
  data: ServiceItem[];
  meta: { total: number; page: number; limit: number; pages: number };
}

// ─── Facturación Electrónica ──────────────────────────────────────────────────

export enum InvoiceStatus {
  EMITIDA    = 'EMITIDA',
  AUTORIZADA = 'AUTORIZADA',
  ANULADA    = 'ANULADA',
}

export interface TaxInfo {
  rucOrId: string;
  businessName: string;
  email: string;
  direccion: string;
}

export interface BillingInvoice {
  id: string;
  billingAccountId: string;
  invoiceNumber: string | null;
  authorizationCode: string | null;
  emittedAt: string | null;
  invoiceStatus: InvoiceStatus;
  amount: number;
  accessKey: string | null;
  xmlContent: string | null;
  issuedById: string | null;
  taxInfo: TaxInfo;
  createdAt: string;
}

export type IssueTaxInfoDto = {
  businessName: string;
  rucOrId: string;
  direccion: string;
  email: string;
};

export type IssueInvoiceDto = {
  receiptIds: string[];
  taxInfo: IssueTaxInfoDto;
};

// ─── Plan with Schedule ───────────────────────────────────────────────────────

export type WeeklyTimeRange = { inicio: string; fin: string };

export type WeeklyDayConfig = { disponible: boolean; franjas: WeeklyTimeRange[] };

export type WeeklyScheduleConfig = {
  repeticion: 'semanal';
  zonaHoraria: string;
  monday: WeeklyDayConfig;
  tuesday: WeeklyDayConfig;
  wednesday: WeeklyDayConfig;
  thursday: WeeklyDayConfig;
  friday: WeeklyDayConfig;
  saturday: WeeklyDayConfig;
  sunday: WeeklyDayConfig;
};

export type SessionOverride = {
  sessionIndex: number;
  fecha: string;
  hora: string;
};

export type CreatePlanWithScheduleDto = {
  titulo: string;
  numeroSesiones: number;
  duracionMinutos: number;
  professionalId: string;
  startDate: string;
  maxSesionesPorDia?: number;
  weeklySchedule: WeeklyScheduleConfig;
  overrides?: SessionOverride[];
  observaciones?: string;
};

export type PlanWithScheduleResult = {
  plan: TreatmentPlan;
  sessions: Session[];
  appointments: Appointment[];
};
